import { useCallback, useState } from "react";
import { descargarCartaDocx } from "@/lib/api";
import { extractApiError } from "@/lib/utils";

interface DescargaState {
  descargando: boolean;
  error: string | null;
  descargar: () => Promise<void>;
}

/**
 * Hook centralizado para la descarga del DOCX de la carta proyectada.
 *
 * Encapsula el patrón `useState(descargando) + try/catch + finally` que
 * estaba duplicado en `StickyActions` y `CartaProyectadaCalm`. El hook
 * gestiona también el error legible y deja que el caller decida si lo
 * muestra o lo ignora.
 *
 * @example
 * const { descargar, descargando, error } = useDescargaCarta(
 *   expediente.expediente_id,
 *   `GEMA_${empresa}_${radicado}.docx`,
 * );
 */
export function useDescargaCarta(
  expedienteId: string,
  nombreArchivo: string,
): DescargaState {
  const [descargando, setDescargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const descargar = useCallback(async () => {
    setError(null);
    setDescargando(true);
    try {
      await descargarCartaDocx(expedienteId, nombreArchivo);
    } catch (e) {
      setError(extractApiError(e, "Error al descargar la carta"));
    } finally {
      setDescargando(false);
    }
  }, [expedienteId, nombreArchivo]);

  return { descargando, error, descargar };
}
